import { Box, Button, Popup, Table, TableRow, TopBar } from '@sam/components';
import { TableHeader, TableKey } from '@sam/components/src/Table/Table.types';
import { useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  createAndSendDunning,
  downloadFile,
  generateNotification,
  getDunningPreviewDocument,
  getInvoicePDFDocument,
  Language,
  MailConfiguration,
  MailType,
  Offer,
  Order,
  Project,
  Receipt,
  updateSchambeckUserTableConfig,
  useData,
} from 'shared';
import { NotificationType } from 'shared/src/notification/notification.types';
import { MailPopup } from '../../components/mailPopup/MailPopup';
import { useUser } from '../../components/UserContext';

import { convertDueInvoicesIntoTableEntries } from '../../utils/invoice/Invoice.utils';
import { generateEmptyMailConfiguration } from '../../utils/mail/Mail.utils';

export const DunningOverview: React.FC = () => {
  const { t } = useTranslation();
  const { axios, user, updateContextUser } = useUser();
  const [selectedReceipt, setSelectedReceipt] = useState<string>('');
  const [mailConfig, setMailConfig] = useState<MailConfiguration>(
    generateEmptyMailConfiguration({
      type: MailType.DUNNING,
    }),
  );
  const [documentToShow, setDocumentToShow] = useState<string>();
  const [filterValue, setFilterValue] = useState<string>('');
  const [showRecipientPopup, toggleShowRecipientPopup] = useState<boolean>(false);
  const { data: dueInvoices, mutate: updateDueInvoices } = useData('INVOICE_DUE', {
    config: { fallbackData: [] },
  });
  const { data: allPayments } = useData('PAYMENTS_ALL', {
    config: { fallbackData: [] },
  });
  const { data: allCustomers } = useData('CUSTOMER_ALL', {
    config: { fallbackData: [] },
  });
  const { data: allCustomerContacts } = useData('CUSTOMER_USERS_ALL_ACTIVE', {
    config: { fallbackData: [] },
  });
  const { data: allCurrencies } = useData('CURRENCY_ALL', {
    config: { fallbackData: [] },
  });
  const { data: allCompanies } = useData('COMPANY_ALL', {
    config: { fallbackData: [] },
  });
  const { data: allOffers } = useData('OFFER_ALL', {
    config: { fallbackData: [] },
  });
  const { data: allProjects } = useData('PROJECT_ALL', {
    config: { fallbackData: [] },
  });

  const { data: allOrders } = useData('ORDER_ALL', {
    config: { fallbackData: [] },
  });
  const { data: allDunningLevels } = useData('DUNNING_ALL', {
    config: { fallbackData: [] },
  });

  const maxDunningLevel: number = useMemo(
    (): number => allDunningLevels.sort((a, b) => b.level - a.level)[0]?.level,
    [allDunningLevels],
  );

  /**
   * Callback method to handle the selection of a receipt for dunning
   * @param receipt selected receipt
   * @param order to get the invoiceRecipient
   */
  const handleReceiptSelect = useCallback(
    (receipt: Receipt, order: Order): void => {
      const relatedProject: Project | undefined = allProjects.find(
        (project) => project.id === receipt.projectId,
      );
      const acceptedOffer: Offer | undefined = allOffers.find(
        (offer) => offer.id === relatedProject?.acceptedOfferId,
      );

      const dunningRecipient: string | undefined = allCustomerContacts.find(
        (contact) =>
          contact.customerId === order.invoiceRecipient.customerId && contact.defaultDunningContact,
      )?.contact.mail;
      if (dunningRecipient) {
        setMailConfig({
          ...mailConfig,
          receiver: dunningRecipient,
          language: acceptedOffer?.language || user.language || Language.DE,
        });
      } else {
        const defaultInvoiceRecipient: string | undefined = allCustomerContacts.find(
          (contact) =>
            contact.customerId === order.invoiceRecipient.customerId &&
            contact.defaultInvoiceContact,
        )?.contact.mail;
        if (defaultInvoiceRecipient) {
          setMailConfig({
            ...mailConfig,
            receiver: defaultInvoiceRecipient,
            language: acceptedOffer?.language || user.language || Language.DE,
          });
          toggleShowRecipientPopup(true);
        } else {
          const receiptRecipient: string | undefined = allCustomerContacts.find(
            (contact) => contact.id === order.invoiceRecipient.customerUserId,
          )?.contact.mail;
          if (receiptRecipient) {
            setMailConfig({
              ...mailConfig,
              receiver: receiptRecipient,
              language: acceptedOffer?.language || user.language || Language.DE,
            });
            toggleShowRecipientPopup(true);
          } else
            generateNotification({
              type: NotificationType.ERROR,
              value: t('general.notification.error.noRecipientFound'),
            });
        }
      }
      setSelectedReceipt(receipt.id);
    },

    [allCustomerContacts, allOffers, allProjects, mailConfig, t, user.language],
  );

  /**
   * helper method to get the preview of a receipt
   * @param invoiceId id of the receipt to load
   */
  const loadReceiptDocument = useCallback(
    async (invoiceId: string): Promise<void> => {
      getInvoicePDFDocument(axios, invoiceId, user.id).then(
        (document) => document && setDocumentToShow(URL.createObjectURL(document)),
      );
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [user.id],
  );
  /**
   * Holds due invoices as TableRows
   */
  const rows: TableRow[] = useMemo(
    (): TableRow[] =>
      convertDueInvoicesIntoTableEntries(
        dueInvoices,
        handleReceiptSelect,
        allOrders,
        allCustomers,
        maxDunningLevel,
        allCompanies,
        allCurrencies,
        allPayments,
        loadReceiptDocument,
      ),
    [
      allCompanies,
      allCurrencies,
      allCustomers,
      allOrders,
      allPayments,
      dueInvoices,
      loadReceiptDocument,
      handleReceiptSelect,
      maxDunningLevel,
    ],
  );

  /**
   * Submit method to create the selected dunnings and send them via mail
   * @returns Promise that is used for the button loading state
   */
  const handleSendMail = async (): Promise<void> => {
    return createAndSendDunning(axios, {
      ...mailConfig,
      activeUserId: user.id,
      invoiceId: selectedReceipt,
    }).then((success) => {
      if (success) {
        generateNotification({
          type: NotificationType.SUCCESS,
          value: t('general.notification.success.sendDunnings'),
        });
        updateDueInvoices();
        setSelectedReceipt('');
      }
    });
  };

  /**
   * Util method to get and download the preview of the dunning
   */
  const handlePreviewDownload = () => {
    getDunningPreviewDocument(
      axios,
      selectedReceipt,
      user.id,
      mailConfig.language || Language.DE,
    ).then((document) => {
      document && downloadFile(document, t('pages.dunningOverview.dunningFilename'));
    });
  };
  return (
    <>
      <TopBar title={t('pages.dunningOverview.topBarHeadline')} onSearch={setFilterValue} />

      <Popup isOpen={!!documentToShow} onClose={() => setDocumentToShow(undefined)}>
        <object data={documentToShow} width="1000px" height="700px" />
      </Popup>
      <Popup
        onClose={() => toggleShowRecipientPopup(false)}
        isOpen={showRecipientPopup}
        buttons={[
          <Button
            value={t('general.buttons.cancel')}
            onClick={() => {
              setMailConfig(generateEmptyMailConfiguration({ type: MailType.DUNNING }));
              setSelectedReceipt('');
              toggleShowRecipientPopup(false);
            }}
          />,
          <Button
            value={t('general.buttons.confirm')}
            onClick={() => toggleShowRecipientPopup(false)}
          />,
        ]}
      >
        <p>
          {t('pages.dunningOverview.recipientInfo', {
            replace: { MAIL: mailConfig.receiver },
          })}
        </p>
      </Popup>
      <MailPopup
        isOpen={!!selectedReceipt && !showRecipientPopup}
        mailConfig={mailConfig}
        toggleOpen={() => setSelectedReceipt('')}
        sendMail={handleSendMail}
        userId={user.id}
        referenceId={selectedReceipt}
        updateConfig={setMailConfig}
        download={handlePreviewDownload}
      />
      <Box>
        <Table
          filterValue={filterValue}
          tableConfig={user.tableConfig?.get(TableKey.DUNNING_OVERVIEW)}
          updateTableConfig={(updatedHeader) => {
            updateSchambeckUserTableConfig(
              axios,
              user,
              TableKey.DUNNING_OVERVIEW,
              updatedHeader,
            ).then((updatedUser) => updatedUser && updateContextUser(updatedUser));
          }}
          rows={rows}
          header={
            t('pages.dunningOverview.tableHeader', {
              returnObjects: true,
            }) as TableHeader[]
          }
        />
      </Box>
    </>
  );
};
